/**
 * Pure aggregation for the Health Trends view.
 *   - buildChartData(allItems, providerHint) — group raw stat items into per-metric series.
 *   - buildSummaryCards(chartData) — 30-day average cards with trend + unit conversion.
 * providerHint is the active wearable's provider (used to synthesize a source for
 * legacy integration-synced items that lack a `source` field). Passed in instead of
 * reading component state so this stays pure.
 */
import { SOURCE_LABELS } from '../constants';
import { metricOf, providerOf } from './metric-keys';
import { getSourceColor } from './source-color';

export type ChartData = Record<string, { date: string; value: number }[]>;

export interface SummaryCard {
  source: string;
  label: string;
  provider: string;
  latest: number;
  unit: string;
  trend: number;
  color: ReturnType<typeof getSourceColor>;
  isPrimary: boolean;
}

// Summary stats — 30-day averages, primary scores first
// Primary metrics shown as large cards; secondary metrics shown as smaller rows
export const PRIMARY_METRICS = new Set([
  // Oura
  'readinessScore', 'sleepScore', 'activityScore', 'heartRate', 'spo2Average', 'steps', 'stressHigh',
  // Whoop — chart keys are provider/metric (slash), so PRIMARY_METRICS must use
  // the same slash form to match (consistent with CHART_ORDER; #1393). The old
  // colon keys never matched `whoop/<metric>`, leaving WHOOP cards stuck in the
  // secondary section.
  'whoop/strain', 'whoop/recovery', 'whoop/sleep',
  // Manual
  'calories', 'protein', 'exercise', 'weight', 'systolic', 'diastolic',
]);

export const METRIC_UNITS: Record<string, string> = {
  readinessScore: '', sleepScore: '', activityScore: '', heartRate: 'bpm', spo2Average: '%',
  steps: 'steps', activeCalories: 'cal', totalCalories: 'cal', equivalentWalkingDistance: 'm',
  stressHigh: 'min', recoveryHigh: 'min',
  sleepDuration: 'hrs', sleepDurationMs: 'hrs', sleepDeepDuration: 'hrs', sleepRemDuration: 'hrs', sleepLightDuration: 'hrs',
  sleepAwakeTime: 'min', sleepEfficiencyPct: '%', sleepAverageHRV: 'ms', sleepAverageHR: 'bpm',
  sleepLowestHR: 'bpm', sleepAverageBreathRate: '/min', sleepRestlessPeriods: '',
  deepSleep: '', remSleep: '', sleepLatency: '', sleepRestfulness: '', sleepTiming: '', totalSleep: '',
  temperatureDeviation: '°C', temperatureTrendDeviation: '°C',
  highActivityTime: 'min', mediumActivityTime: 'min', lowActivityTime: 'min',
  sedentaryTime: 'min', restingTime: 'min',
  calories: 'cal/day', protein: 'g/day', exercise: 'cal/day', weight: 'lbs',
  systolic: 'mmHg', diastolic: 'mmHg',
};

// Convert time-based metrics to display-friendly values
export const SEC_TO_MIN_METRICS = new Set(['stressHigh', 'recoveryHigh', 'sleepAwakeTime', 'highActivityTime', 'mediumActivityTime', 'lowActivityTime', 'sedentaryTime', 'restingTime']);
export const SEC_TO_HRS_METRICS = new Set(['sleepDuration', 'sleepDeepDuration', 'sleepRemDuration', 'sleepLightDuration']);
export const MS_TO_HRS_METRICS = new Set(['sleepDurationMs']);

/**
 * Sparkline data — group by source for chart.
 * Trends: show weight, calories, exercise, BP + Whoop metrics.
 * @param providerHint active wearable provider, used for legacy-source synthesis.
 */
export function buildChartData(allItems: any[], providerHint?: string): ChartData {
  const bySource: ChartData = {};
  const caloriesByDate: Record<string, number> = {};
  const proteinByDate: Record<string, number> = {};
  const burnedByDate: Record<string, number> = {};

  for (const item of allItems) {
    let source = item.source || item.attributes?.source || '';
    // Synthesize source for integration-synced items missing the source field (legacy data)
    if (!source && (item.integrationId || item.attributes?.integrationId)) {
      const category = item.category || item.attributes?.category || 'data';
      const endpointId = item.endpointId || item.attributes?.endpointId || '';
      // Detect provider from endpointId patterns or active wearable
      const provider = providerHint || (endpointId.includes('daily_') || endpointId.includes('heart_rate') || endpointId.includes('spo2') ? 'oura' : 'wearable');
      source = `${provider}:${category}`;
    }
    const date = (item.date || item.attributes?.date || '').slice(0, 10);
    const value = item.value ?? item.attributes?.value;
    if (!source || !date) continue;

    // Aggregate daily calories from food entries
    if (source === 'health:food') {
      const cal = item.totalCalories || item.attributes?.totalCalories || 0;
      const prot = item.totalProtein || item.attributes?.totalProtein || 0;
      caloriesByDate[date] = (caloriesByDate[date] || 0) + cal;
      proteinByDate[date] = (proteinByDate[date] || 0) + prot;
      continue;
    }

    // Aggregate daily exercise burned
    if (source === 'health:exercise') {
      const burned = item.totalCaloriesBurned || item.attributes?.totalCaloriesBurned || 0;
      burnedByDate[date] = (burnedByDate[date] || 0) + burned;
      continue;
    }

    // Weight and BP — direct values
    if (source === 'health:vitals') {
      const label = (item.label || item.attributes?.label || '').toLowerCase();
      if (label.includes('weight') && value) {
        if (!bySource['weight']) bySource['weight'] = [];
        bySource['weight'].push({ date, value: Number(value) });
      }
      if (label.includes('blood') || item.systolic || item.attributes?.systolic) {
        const sys = item.systolic || item.attributes?.systolic;
        const dia = item.diastolic || item.attributes?.diastolic;
        if (sys) {
          if (!bySource['systolic']) bySource['systolic'] = [];
          bySource['systolic'].push({ date, value: Number(sys) });
        }
        if (dia) {
          if (!bySource['diastolic']) bySource['diastolic'] = [];
          bySource['diastolic'].push({ date, value: Number(dia) });
        }
      }
      continue;
    }

    // Wearable and other sources (skip labs — not chartable)
    if (source === 'health:labs') continue;
    if (value !== undefined) {
      // For wearable data, group by metric name (not source category) so each metric
      // gets its own chart line. E.g. readinessScore vs temperatureDeviation instead
      // of all being lumped under "oura:readiness".
      // Prefix with provider (e.g. "oura/restingHeartRate") so overlapping metrics
      // from different wearables are distinguishable and independently togglable.
      const metric = item.title || item.label || item.attributes?.label || '';
      const WEARABLE_PREFIXES = ['oura:', 'fitbit:', 'strava:'];
      const isWearable = WEARABLE_PREFIXES.some(p => source.startsWith(p)) || source.startsWith('whoop:');
      const provider = isWearable ? source.split(':')[0] : '';
      const chartKey = isWearable && metric ? (provider ? `${provider}/${metric}` : metric) : source;
      if (!bySource[chartKey]) bySource[chartKey] = [];
      bySource[chartKey].push({ date, value: Number(value) });
    }
  }

  // Convert aggregated daily totals to chart series
  for (const [date, cal] of Object.entries(caloriesByDate)) {
    if (!bySource['calories']) bySource['calories'] = [];
    bySource['calories'].push({ date, value: cal });
  }
  for (const [date, prot] of Object.entries(proteinByDate)) {
    if (prot > 0) {
      if (!bySource['protein']) bySource['protein'] = [];
      bySource['protein'].push({ date, value: prot });
    }
  }
  for (const [date, burned] of Object.entries(burnedByDate)) {
    if (!bySource['exercise']) bySource['exercise'] = [];
    bySource['exercise'].push({ date, value: burned });
  }

  return bySource;
}

/** Summary stats — 30-day averages, primary scores first. */
export function buildSummaryCards(chartData: ChartData): SummaryCard[] {
  const cards: SummaryCard[] = [];
  for (const [key, points] of Object.entries(chartData)) {
    if (points.length < 2) continue;
    let avg = points.reduce((s, p) => s + p.value, 0) / points.length;
    // Convert units for display
    const m = metricOf(key);
    if (SEC_TO_MIN_METRICS.has(m)) avg = avg / 60;
    if (SEC_TO_HRS_METRICS.has(m)) avg = avg / 3600;
    if (MS_TO_HRS_METRICS.has(m)) avg = avg / 3_600_000;
    avg = Math.round(avg * 10) / 10; // 1 decimal
    if (Number.isInteger(avg)) avg = Math.round(avg);
    // Trend: compare last 7 days avg vs overall
    const sorted = [...points].sort((a, b) => b.date.localeCompare(a.date));
    const recent7 = sorted.slice(0, 7);
    let recentAvg = recent7.length > 0 ? recent7.reduce((s, p) => s + p.value, 0) / recent7.length : avg;
    if (SEC_TO_MIN_METRICS.has(m)) recentAvg = recentAvg / 60;
    if (SEC_TO_HRS_METRICS.has(m)) recentAvg = recentAvg / 3600;
    if (MS_TO_HRS_METRICS.has(m)) recentAvg = recentAvg / 3_600_000;
    recentAvg = Math.round(recentAvg * 10) / 10;
    const trend = Math.round((recentAvg - avg) * 10) / 10;
    const unit = METRIC_UNITS[m] || METRIC_UNITS[key] || '';
    const isPrimary = PRIMARY_METRICS.has(m) || PRIMARY_METRICS.has(key);
    cards.push({
      source: key,
      label: SOURCE_LABELS[m] || SOURCE_LABELS[key] || m.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
      provider: providerOf(key),
      latest: avg,
      unit,
      trend,
      color: getSourceColor(key),
      isPrimary,
    });
  }
  // Sort: primary metrics first, then alphabetical
  cards.sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
  return cards;
}
